import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {LoaderService} from '../../../../services/loader/loader.service';
import {UserService} from '../../../../services/user/user.service';
import {User} from '../../../../models/user';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import {InputSwitchComponent} from '../../../layout/input-switch/input-switch.component';
import {CountryService} from '../../../../services/country/country.service';
import {forkJoin} from 'rxjs';
import {Country} from '../../../../models/country';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {RightService} from '../../../../services/right/right.service';
import {Right} from '../../../../models/right';
import {IsGranted} from '../../../../decorator/is-granted';
import {UploadFileService} from '../../../../services/upload-file/upload-file.service';
import {UploadFile} from '../../../../models/upload-file';
import {AuthService} from '../../../../services/auth/auth.service';
import {ConfirmService} from '../../../../services/confirm/confirm.service';
import {ParentRight} from '../../../../constants/parent-right';
import {ImageService} from '../../../../services/image/image.service';
import {MetaTitle} from '../../../../decorator/meta-title';
import {Hierarchy} from '../../../../models/security/hierarchy';
import {ClipboardService} from '../../../../services/clipboard/clipboard.service';

@Component({
  selector: 'app-user-form',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    InputSwitchComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    FormsModule
],
  templateUrl: './user-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './user-form.component.scss'
})
@IsGranted(['ROLE_API_USER_WRITE', 'USER_VIEW'])
@MetaTitle('User form')
export class UserFormComponent implements OnInit {

  ParentRight = ParentRight;
  hierarchy: Hierarchy = new Hierarchy();

  loaded = false;
  user = new User();
  countries: Country[] = [];
  managers: User[] = [];
  rights: Right[] = [];
  uploadImage: UploadFile | null = null;
  currentParentRight: ParentRight = ParentRight.USER;
  gizmoUrl!: string;

  form = new FormGroup({
    email: new FormControl('', [Validators.required]),
    firstname: new FormControl('', [Validators.required]),
    lastname: new FormControl('', [Validators.required]),
    nickname: new FormControl(''),
    teams: new FormControl(''),
    telegram: new FormControl(''),
    phone: new FormControl(''),
    authorizedCountries: new FormControl<string[]>([]),
    authorizedIps: new FormControl(''),
    enabled: new FormControl(true),
    accountManager: new FormControl(false),
    teamManager: new FormControl(false),
    managerId: new FormControl<number | null>(null),
  });

  constructor(
    protected countryService: CountryService,
    protected imageService: ImageService,
    protected clipboard: ClipboardService,
    private auth: AuthService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private rightService: RightService,
    private uploadFileService: UploadFileService,
    private confirm: ConfirmService
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.countryService.findAll(),
      this.userService.findAll({teamManager: true, sort: 'firstname,lastname'}),
      this.rightService.findAll({sort: 'name'}),
      this.rightService.fetchHierarchy(),
      this.auth.getAsyncConfig()
    ]).subscribe(([countries, managers, rights, hierarchy, config]) => {
      this.countries = countries;
      this.managers = managers;
      this.rights = rights;
      this.hierarchy = hierarchy;
      this.gizmoUrl = config?.data?.gizmoUrl;

      const id = Number(this.route.snapshot.paramMap.get('id'));

      if (id) {
        this.userService.findOneById(id, {groups: 'manager,image'}).subscribe((user: User) => {
          this.user = user;
          this.form.patchValue({
            email: this.user.email,
            firstname: this.user.firstname,
            lastname: this.user.lastname,
            nickname: this.user.nickname,
            teams: this.user.teams,
            telegram: this.user.telegram,
            phone: this.user.phone,
            enabled: this.user.enabled,
            authorizedCountries: this.user.authorizedCountries,
            authorizedIps: this.user.authorizedIps?.join(' '),
            accountManager: this.user.accountManager,
            teamManager: this.user.teamManager,
            managerId: this.user.manager?.id ?? null
          });

          if (this.user.roles.includes(ParentRight.DEV)) {
            this.currentParentRight = ParentRight.DEV;
          } else if (this.user.roles.includes(ParentRight.SUPER_ADMIN)) {
            this.currentParentRight = ParentRight.SUPER_ADMIN;
          } else if (this.user.roles.includes(ParentRight.ADMIN)) {
            this.currentParentRight = ParentRight.ADMIN;
          } else {
            this.currentParentRight = ParentRight.USER;
          }

          this.user.roles = this.hierarchy.deserializeRoles(this.user.roles, this.currentParentRight);

          this.loaded = true;
          this.loader.stopLoading();
        });
      } else {
        this.changeParentRole();
        this.loaded = true;
        this.loader.stopLoading();
      }
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    this.loader.startLoading();

    let params: any = this.form.value;

    params.authorizedIps = params.authorizedIps ? params.authorizedIps?.split(' ') : null;
    params.roles = [this.currentParentRight];
    for (const role of this.user.roles) {
      // We only add roles that are off parentRight
      if (!this.hierarchy[this.currentParentRight].includes(role) && role !== this.currentParentRight) {
        params.roles.push(role);
      }
    }

    if (this.uploadImage) {
      params.image = this.uploadImage;
    }

    if (this.isCreate()) {
      this.userService.create(params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('User has been created.');
        this.router.navigateByUrl('/users/users').then();
      });
    } else if (this.user?.id) {
      this.userService.update(this.user.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('User has been modified.');
        this.router.navigateByUrl('/users/users').then();
      });
    }
  }

  isCreate(): boolean {
    return !this.user.id;
  }

  hasRole(role: string): boolean {
    return this.userService.hasRole(this.user, role);
  }

  setRole(role: string, checked: boolean): void {
    if (checked) {
      this.userService.addRole(this.user, role);
    } else {
      this.userService.removeRole(this.user, role);
    }
  }

  profilePictureUpload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadImage = uploadFile;
    });
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_USER_WRITE']);
  }

  canChangeDev(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_DEV']) || !this.hasRole('ROLE_API_DEV');
  }

  changeParentRole(): void {
    this.user.roles = [...this.hierarchy[this.currentParentRight]];
  }

  isRoleDisabled(role: string): boolean {
    return this.hierarchy[this.currentParentRight].includes(role);
  }

  regeneratePassword(): void {
    this.confirm.open('Do you want to regenerate this user\'s password?').subscribe((result: boolean) => {
      if (result && this.user.id) {
        this.userService.update(this.user.id, {regeneratePassword: true}).subscribe(() => {
          this.snackbar.success(
            'The password has been reset successfully. ' +
            'An email has been send to the user with the new password.'
          );
        });
      }
    });
  }

  getRegisterUrl(): string {
    return this.gizmoUrl + '/register?amid=' + this.user.id;
  }
}
